import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Monitor, Globe, Server, Network,
    Shield, Lock, LogIn, Calendar, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    CheckCircle2, XCircle, X, Fingerprint, KeyRound,
    AlertTriangle, Tag, ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { useClientLevel } from "../../hooks/useClientLevel";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";

interface ADComputer {
    id: number;
    created: string;
    updated: string;
    agent_id: number;
    client_id: number;
    asset_id: number;
    ad_domain_guid: string;
    name: string;
    member_of: string;
    domain: string;
    dns_host_name: string;
    operating_system: string;
    operating_system_version: string;
    is_critical_system_object: boolean;
    last_logon_timestamp_pshell: string;
    last_logon_date: string;
    distinguished_name: string;
    sam_account_name: string;
    object_guid: string;
    account_disabled: boolean;
    enabled: boolean;
    created_when: string;
    modified_when: string;
    locked_out: boolean;
    password_expired: boolean;
    pwd_last_set_time: string;
    password_never_expires: boolean;
    password_not_required: boolean;
}

const Val = ({ v }: { v: string | number | undefined | null }) => {
    const empty = v === undefined || v === null || v === "-" || v === "";
    return (
        <span className={empty ? "text-gray-600" : "text-gray-200"}>
            {empty ? "—" : String(v)}
        </span>
    );
};

const BoolBadge = ({
    value, trueLabel = "Yes", falseLabel = "No",
    trueColor = "text-green-400", falseColor = "text-gray-600"
}: {
    value: boolean;
    trueLabel?: string;
    falseLabel?: string;
    trueColor?: string;
    falseColor?: string;
}) => (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${value ? trueColor : falseColor}`}>
        {value ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
        {value ? trueLabel : falseLabel}
    </span>
);

// ─── Detail Panel ─────────────────────────────────────────────────────────────
function ComputerDetailPanel({ computer, onClose }: { computer: ADComputer; onClose: () => void }) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const Section = ({ title, icon, colorClass = "text-blue-400" }: { title: string; icon: React.ReactNode; colorClass?: string }) => (
        <div className="flex items-center gap-2 mb-3 mt-5 first:mt-0">
            <span className={colorClass}>{icon}</span>
            <span className="text-[9px] font-bold text-gray-200 uppercase tracking-widest">{title}</span>
            <div className="flex-1 h-px bg-gray-700/50" />
        </div>
    );

    const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <div className="flex flex-col gap-0.5">
            <span className="text-[9px] text-gray-500 uppercase tracking-widest">{label}</span>
            <div className="text-[11px]">{children}</div>
        </div>
    );

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-[1px]" onClick={onClose} />

            {/* Panel */}
            <div className="fixed top-0 right-0 h-full w-[30%] min-w-[340px] bg-gray-900 border-l border-gray-700 z-55 flex flex-col shadow-2xl animate-slide-in-right">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60 bg-gray-800/60 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
                            <Monitor size={16} className="text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-[13px] font-bold text-white">{computer.name}</p>
                            <p className="text-[10px] font-mono text-gray-200">{computer.sam_account_name}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-gray-200 hover:text-white hover:bg-gray-700 transition-all">
                        <X size={15} />
                    </button>
                </div>

                {/* Pills row */}
                <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-gray-700/40 bg-gray-800/30 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold border ${computer.enabled
                        ? "bg-green-500/10 border-green-500/20 text-green-400"
                        : "bg-red-500/10 border-red-500/20 text-red-400"
                        }`}>
                        {computer.enabled ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
                        {computer.enabled ? "Enabled" : "Disabled"}
                    </span>
                    {computer.locked_out && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-yellow-500/10 border border-yellow-500/20 text-yellow-400">
                            <AlertTriangle size={9} /> Locked Out
                        </span>
                    )}
                    {computer.is_critical_system_object && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-red-500/10 border border-red-500/20 text-red-400">
                            <Shield size={9} /> Critical
                        </span>
                    )}
                    {computer.password_expired && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold bg-orange-500/10 border border-orange-500/20 text-orange-400">
                            <KeyRound size={9} /> Pwd Expired
                        </span>
                    )}
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1 text-gray-200">

                    {/* Identity */}
                    <Section title="Identity" icon={<Monitor size={12} />} colorClass="text-blue-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Name"><Val v={computer.name} /></Field>
                        <Field label="SAM Account"><span className="font-mono text-gray-300"><Val v={computer.sam_account_name} /></span></Field>
                        <Field label="DNS Hostname"><span className="font-mono text-[10px] text-cyan-300/80 break-all"><Val v={computer.dns_host_name} /></span></Field>
                        <Field label="Domain"><Val v={computer.domain} /></Field>
                        <Field label="Member Of"><Val v={computer.member_of} /></Field>
                    </div>

                    {/* OS */}
                    <Section title="Operating System" icon={<Server size={12} />} colorClass="text-indigo-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="OS">
                            <span className="text-emerald-300"><Val v={computer.operating_system} /></span>
                        </Field>
                        <Field label="Version">
                            <span className="font-mono text-[10px] text-gray-200"><Val v={computer.operating_system_version} /></span>
                        </Field>
                    </div>

                    {/* Account Status */}
                    <Section title="Account Status" icon={<Lock size={12} />} colorClass="text-rose-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Enabled">
                            <BoolBadge value={computer.enabled} trueColor="text-green-400" falseColor="text-red-400" />
                        </Field>
                        <Field label="Account Disabled">
                            <BoolBadge value={computer.account_disabled} trueColor="text-red-400" falseColor="text-gray-600" />
                        </Field>
                        <Field label="Locked Out">
                            <BoolBadge value={computer.locked_out} trueColor="text-yellow-400" falseColor="text-gray-600" />
                        </Field>
                        <Field label="Critical Object">
                            <BoolBadge value={computer.is_critical_system_object} trueColor="text-red-400" falseColor="text-gray-600" />
                        </Field>
                    </div>

                    {/* Password */}
                    <Section title="Password" icon={<KeyRound size={12} />} colorClass="text-amber-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Last Set">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={computer.pwd_last_set_time} /></span>
                        </Field>
                        <Field label="Expired">
                            <BoolBadge value={computer.password_expired} trueColor="text-red-400" falseColor="text-gray-600" />
                        </Field>
                        <Field label="Never Expires">
                            <BoolBadge value={computer.password_never_expires} trueColor="text-yellow-400" falseColor="text-gray-600" />
                        </Field>
                        <Field label="Not Required">
                            <BoolBadge value={computer.password_not_required} trueColor="text-orange-400" falseColor="text-gray-600" />
                        </Field>
                    </div>

                    {/* Logon Activity */}
                    <Section title="Logon Activity" icon={<LogIn size={12} />} colorClass="text-cyan-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Last Logon">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={computer.last_logon_date} /></span>
                        </Field>
                        <Field label="Last Logon (PS)">
                            <span className="font-mono text-[10px] text-gray-200"><Val v={computer.last_logon_timestamp_pshell} /></span>
                        </Field>
                    </div>

                    {/* Timestamps */}
                    <Section title="Timestamps" icon={<Calendar size={12} />} colorClass="text-purple-400" />
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        <Field label="Created">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={computer.created_when} /></span>
                        </Field>
                        <Field label="Modified">
                            <span className="font-mono text-[10px] text-gray-300"><Val v={computer.modified_when} /></span>
                        </Field>
                    </div>

                    {/* Identifiers */}
                    <Section title="Identifiers" icon={<Fingerprint size={12} />} colorClass="text-emerald-400" />
                    <div className="flex flex-col gap-3">
                        <Field label="Object GUID">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={computer.object_guid} /></span>
                        </Field>
                        <Field label="Distinguished Name">
                            <span className="font-mono text-[10px] text-gray-200 break-all"><Val v={computer.distinguished_name} /></span>
                        </Field>
                    </div>

                    <div className="h-6" />
                </div>
            </div>
        </>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ADComputers({ asset_id }: { asset_id: any }) {
    const [computers, setComputers] = useState<ADComputer[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedComputer, setSelectedComputer] = useState<ADComputer | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    const level = useClientLevel();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchComputers = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;

            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { sam_account_name: { $ilike: `%${debouncedSearch}%` } },
                    { dns_host_name: { $ilike: `%${debouncedSearch}%` } },
                    { domain: { $ilike: `%${debouncedSearch}%` } },
                    { operating_system: { $ilike: `%${debouncedSearch}%` } },
                    { distinguished_name: { $ilike: `%${debouncedSearch}%` } },
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/ad_computers", { params });

            if (response.data.status) {
                setComputers(response.data.data);
                setTotalRecords(response.data.total ?? response.data.data.length);
            } else {
                showError("Failed to load AD computers");
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [level, debouncedSearch, currentPage, limit, sortConfig, asset_id]);

    useEffect(() => {
        fetchComputers();
    }, [fetchComputers]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest cursor-pointer group select-none"
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {label}
                    <div className="flex items-center ml-auto">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-emerald-400" />
                            ) : (
                                <ChevronDown size={12} className="text-emerald-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };


    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {selectedComputer && (
                <ComputerDetailPanel
                    computer={selectedComputer}
                    onClose={() => setSelectedComputer(null)}
                />
            )}

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-emerald-600/20 p-1.5 rounded-lg border border-emerald-500/30">
                        <Monitor size={16} className="text-emerald-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">AD Computers</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-emerald-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search computers..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchComputers}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-emerald-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="Computer" sortKey="name" icon={<Monitor size={13} className="text-emerald-400" />} />
                                <SortableHeader label="Domain" sortKey="domain" icon={<Globe size={13} className="text-indigo-400" />} />
                                <SortableHeader label="DNS Hostname" sortKey="dns_host_name" icon={<Server size={13} className="text-cyan-400" />} />
                                <SortableHeader label="OS" sortKey="operating_system" icon={<Network size={13} className="text-blue-400" />} />
                                <SortableHeader label="Status" sortKey="enabled" icon={<Lock size={13} className="text-red-400" />} />
                                <SortableHeader label="Password" sortKey="pwd_last_set_time" icon={<KeyRound size={13} className="text-purple-400" />} />
                                <SortableHeader label="Last Logon" sortKey="last_logon_date" icon={<LogIn size={13} className="text-orange-400" />} />
                                <SortableHeader label="Critical" sortKey="is_critical_system_object" icon={<Tag size={13} className="text-red-400" />} />
                                <SortableHeader label="Created" sortKey="created_when" icon={<Calendar size={13} className="text-pink-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading computers...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : computers.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Monitor size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                {debouncedSearch ? `No results for "${debouncedSearch}"` : "No computers found"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                computers.map((computer) => (
                                    <tr
                                        key={computer.id}
                                        onClick={() => setSelectedComputer(computer)}
                                        className={`hover:bg-gray-800/60 transition-colors cursor-pointer ${selectedComputer?.id === computer.id
                                            ? "bg-emerald-500/5 border-l-2 border-l-emerald-500"
                                            : ""
                                            }`}
                                    >
                                        {/* Computer */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                                                    <Monitor size={11} className="text-emerald-400" />
                                                </div>
                                                <div>
                                                    <p className="text-[11px] font-bold text-gray-100">{computer.name}</p>
                                                    <p className="text-[9px] font-mono text-gray-500">{computer.sam_account_name}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Domain */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] text-gray-300"><Val v={computer.domain} /></span>
                                        </td>

                                        {/* DNS Hostname */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[11px] font-mono text-cyan-300/80"><Val v={computer.dns_host_name} /></span>
                                        </td>

                                        {/* OS */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-[11px] text-gray-200"><Val v={computer.operating_system} /></span>
                                                {computer.operating_system_version && computer.operating_system_version !== "-" && (
                                                    <span className="text-[9px] font-mono text-gray-500">
                                                        v{computer.operating_system_version}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${computer.enabled ? "text-green-400" : "text-red-400"}`}>
                                                    {computer.enabled ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                                                    {computer.enabled ? "Enabled" : "Disabled"}
                                                </span>
                                                {computer.locked_out && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-400">
                                                        <AlertTriangle size={11} /> Locked
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Password */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                {computer.password_expired && (
                                                    <span className="text-[9px] font-bold text-red-400">Expired</span>
                                                )}
                                                {computer.password_never_expires && (
                                                    <span className="text-[9px] font-bold text-yellow-400/80">Never Expires</span>
                                                )}
                                                {computer.password_not_required && (
                                                    <span className="text-[9px] font-bold text-orange-400/80">Not Required</span>
                                                )}
                                                <span className="text-[9px] font-mono text-gray-500">
                                                    set: <Val v={computer.pwd_last_set_time} />
                                                </span>
                                            </div>
                                        </td>

                                        {/* Last Logon */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className="text-[10px] font-mono text-gray-300">
                                                <Val v={computer.last_logon_date} />
                                            </span>
                                        </td>

                                        {/* Critical */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {computer.is_critical_system_object ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-400">
                                                    <CheckCircle2 size={11} /> Yes
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-300">
                                                    <XCircle size={11} /> No
                                                </span>
                                            )}
                                        </td>

                                        {/* Created */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-[10px] font-mono text-gray-200">{computer.created_when || "—"}</span>
                                                {computer.modified_when && computer.modified_when !== computer.created_when && (
                                                    <span className="text-[9px] font-mono text-gray-600">mod: {computer.modified_when}</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-500 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="text-[9px] text-emerald-400 hover:text-emerald-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}